import { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "../lib/supabase";
import { getCache, setCache, CONVERSATIONS_CACHE_TTL } from "../lib/dmCacheService";
import { toOrgStartISO, toOrgEndISO } from "../lib/timezone";
import { logger } from "../lib/logger";

// =============================================================================
// useDismissedConversations
// =============================================================================
// Reads the pile of dismissed/killed conversations that EVERY other DM view
// deliberately filters out. Powers the "Dismissed Leads" panel at the bottom of
// the Setter Performance tab.
//
// Reads the BASE table ghl_conversations directly — the dismiss columns live
// there, the RLS policy `users_view_org_conversations` grants authenticated
// org-scoped SELECT, and the rest of the DM UI (ConversationViewModal,
// ProofGridView, useConversationDismissal) already reads it the same way. No
// readable-view change, no migration — the columns already exist where we read.
//
// - Scope: org + the assigned setters shown in the tab (RBAC resolved upstream
//   into `setterIds`).
// - "Dismissed in period" = suffering_dismissed_at within the selected range,
//   org-tz aware via toOrgStartISO/toOrgEndISO (same window as the scorecards).
// - Dismissals are sparse → single capped query, SWR-cached 5min. No cachebump:
//   independent surface, its own versioned cache key (CACHE_VERSION in prefix).
// =============================================================================

export interface DismissedConversation {
  id: string; // ghl_conversations UUID — feeds ConversationViewModal.conversationId
  contact_name: string | null;
  ghl_assigned_user_id: string | null; // GHL user id; map to a name via the tab's metrics
  dismiss_type: "dismissed" | "dead" | null;
  suffering_dismissed_at: string | null;
  last_message_at: string | null;
}

interface Params {
  organizationId: string | null;
  setterIds: string[];
  dateRange: { start: Date | null; end: Date | null };
  timezone: string;
  // When true, fetch every dismissed conversation in the org (including
  // unassigned rows) instead of filtering to `setterIds`. Used by the
  // org-scoped grid modal + panel list — see S4b-grid-scope: `.in(setterIds)`
  // never matches a NULL ghl_assigned_user_id, so setter-scoped fetches are
  // blind to unassigned dismissals even though the leaderboard counts them.
  orgScope?: boolean;
}

const PGRST_MAX = 1000;

export function useDismissedConversations({
  organizationId,
  setterIds,
  dateRange,
  timezone,
  orgScope,
}: Params) {
  const [rows, setRows] = useState<DismissedConversation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const genRef = useRef(0);

  const startISO = dateRange.start ? toOrgStartISO(dateRange.start, timezone) : null;
  const endISO = dateRange.end ? toOrgEndISO(dateRange.end, timezone) : null;

  const fetchRows = useCallback(async () => {
    // Under orgScope, setterIds is irrelevant — do NOT bail on an empty
    // roster (S4b-grid-scope: the old unconditional `setterIds.length === 0`
    // bail here would re-hide unassigned rows even after the `.in()` filter
    // below is dropped, silently reopening the same contradiction it fixes).
    if (!supabase || !organizationId || !startISO || !endISO) {
      setRows([]);
      return;
    }
    if (!orgScope && setterIds.length === 0) {
      setRows([]);
      return;
    }

    const setterKey = orgScope ? "org" : [...setterIds].sort().join(",");
    const cacheKey = `dismissed_${startISO}_${endISO}_${setterKey}_${orgScope ? "org" : "setters"}`;
    const cached = getCache<DismissedConversation[]>(
      cacheKey,
      CONVERSATIONS_CACHE_TTL,
      organizationId
    );
    if (cached) setRows(cached);

    const myGen = ++genRef.current;
    setIsLoading(true);
    setError(null);
    try {
      let query = supabase
        .from("ghl_conversations")
        .select(
          "id, contact_name, ghl_assigned_user_id, dismiss_type, suffering_dismissed_at, last_message_at"
        )
        .eq("organization_id", organizationId);

      if (!orgScope) {
        query = query.in("ghl_assigned_user_id", setterIds);
      }

      const { data, error: qErr } = await query
        .not("dismiss_type", "is", null)
        .gte("suffering_dismissed_at", startISO)
        .lte("suffering_dismissed_at", endISO)
        .order("suffering_dismissed_at", { ascending: false })
        .range(0, PGRST_MAX - 1);

      if (genRef.current !== myGen) return; // a newer fetch superseded this one
      if (qErr) throw qErr;

      const result = (data || []) as DismissedConversation[];
      setRows(result);
      setCache(cacheKey, result, organizationId);
    } catch (e) {
      if (genRef.current !== myGen) return;
      logger.warn("useDismissedConversations fetch failed", e);
      setError(e instanceof Error ? e.message : "Failed to load dismissed leads");
    } finally {
      if (genRef.current === myGen) setIsLoading(false);
    }
  }, [organizationId, startISO, endISO, setterIds, orgScope]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  return { dismissed: rows, count: rows.length, isLoading, error, refresh: fetchRows };
}
